import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import { listAuctions, createAuction, auctionPrice, timeLeft, type Auction } from "../lib/auctions";
import { LISTING_CATEGORIES } from "../lib/marketplace";

export function Auctions() {
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", category: "trailer", starting_price: "", reserve_price: "", min_increment: "500", ends_in_hours: "72" });

  async function refresh() {
    setAuctions((await listAuctions()).auctions);
  }
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.starting_price.trim()) return;
    setBusy(true);
    try {
      await createAuction({ ...form, ends_in_hours: Number(form.ends_in_hours) });
      setForm({ title: "", category: "trailer", starting_price: "", reserve_price: "", min_increment: "500", ends_in_hours: "72" });
      setShowForm(false);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Auctions"
        subtitle="Timed auctions for trucks, trailers, and equipment."
        actions={
          <div className="flex gap-2">
            <Link to="/auctions/mine"><Button variant="ghost">My auctions</Button></Link>
            <Button onClick={() => setShowForm((v) => !v)}>{showForm ? "Cancel" : "Start an auction"}</Button>
          </div>
        }
      />

      {showForm && (
        <Panel title="New auction">
          <form className="grid gap-3 sm:grid-cols-2" onSubmit={submit}>
            <input className={`${inputClass} sm:col-span-2`} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            <select className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} aria-label="Category">
              {LISTING_CATEGORIES.map((c) => <option key={c} value={c}>{labelize(c)}</option>)}
            </select>
            <input className={inputClass} placeholder="Ends in (hours)" value={form.ends_in_hours} onChange={(e) => setForm({ ...form, ends_in_hours: e.target.value })} />
            <input className={inputClass} placeholder="Starting price" value={form.starting_price} onChange={(e) => setForm({ ...form, starting_price: e.target.value })} required />
            <input className={inputClass} placeholder="Min increment (default $50)" value={form.min_increment} onChange={(e) => setForm({ ...form, min_increment: e.target.value })} />
            <input className={`${inputClass} sm:col-span-2`} placeholder="Reserve price (optional)" value={form.reserve_price} onChange={(e) => setForm({ ...form, reserve_price: e.target.value })} />
            <Button type="submit" disabled={busy || !form.title.trim()} className="sm:col-span-2">{busy ? "Starting…" : "Start auction"}</Button>
          </form>
        </Panel>
      )}

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : auctions.length === 0 ? (
        <p className="text-sm text-muted">No live auctions right now.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {auctions.map((a) => (
            <Link key={a.id} to={`/auctions/${a.id}`} className="grid gap-1 rounded-card border border-line bg-surface p-4 transition-colors hover:border-accent">
              <span className="font-display text-base font-black tracking-tight">{a.title}</span>
              <span className="font-mono text-sm font-bold text-accent-strong">{auctionPrice(a.current_price_cents)}</span>
              <span className="font-mono text-[11px] text-muted">{a.bid_count} bid{a.bid_count === 1 ? "" : "s"} · {timeLeft(a.ends_at)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
